import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { headers } from "../../../App/Axios"
import { JoinStatus, RoleType, RoomType } from "../../../App/enums"
import { organizationProfileDefault, profileDefault } from "../../../App/GlobaleVariables"
import HTTPService from "../../../App/HTTPService"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { RootState } from "../../../App/store"
import { CloseIcon, LockerIconSmall } from "../../Shared/Components/SvgIcons"
import UserCard from "../../Shared/Components/UserCard"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import { setRoomMembers } from "../RoomSlices/MembersInRoomSlice"
import { setRemoveRoomMemberId } from "../RoomSlices/RoomMemberSlice"
import SearchInput from "../../Shared/Primitive/SearchInput"
import RoomMembersMap from "./RoomMembersMap"

function RoomMembers() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [inputState, setInputState] = useState("")
    const translation = useSelector(selectTranslations)
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const isRoomManager = useSelector((state: RootState) => state.roomSlice.roomManagerSlice.room_manager)
    const isRoomModerator = useSelector((state: RootState) => state.roomSlice.RoomModeratorSlice.room_moderator)
    const isRoomOwner = useSelector((state: RootState) => state.roomSlice.roomOwnerSlice.room_owner)
    const Members = useSelector((state: RootState) => state.MembersInRoomSlice.members)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const { roomId, id } = useParams()
    const effectiveRoomId = roomId || id
    const canSeeMembers = roomDataState.type === RoomType.Private ? isMemberInRoom === JoinStatus.MEMBER || isRoomOwner : true
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const hasPrivelegedPermission = isRoomManager || isRoomModerator || isRoomOwner
    const [viewMode, setViewMode] = useState<"list" | "map">("list")
    const membersContainerRef = useRef<HTMLDivElement>(null)

    const onViewModeChange = (nextViewMode: "list" | "map") => {
        setViewMode(nextViewMode)

        if (effectiveRoomId) {
            const storageKey = `room-members-view-mode-${effectiveRoomId}`
            sessionStorage.setItem(storageKey, nextViewMode)
        }
    }

    const getRequest = useCallback(() => {
        return HTTPService.get(`/room/${effectiveRoomId}/members/`, headers)
    }, [effectiveRoomId])

    useEffect(() => {
        getRequest()
            .then(response => {
                if (response.status === 200) {
                    dispatch(setRoomMembers(response.data.user))
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [dispatch, getRequest])

    const searchMembersGetRequest = useCallback(
        (searchQuery: string) => {
            return HTTPService.get(`/room/${effectiveRoomId}/members/?query=${searchQuery}`, headers)
        },
        [effectiveRoomId]
    )

    useEffect(() => {
        const getData = setTimeout(() => {
            searchMembersGetRequest(inputState).then(response => {
                if (response.status === 200) {
                    dispatch(setRoomMembers(response.data.user))
                }
            })
        }, 500)

        return () => clearTimeout(getData)
    }, [inputState, searchMembersGetRequest, dispatch])

    useEffect(() => {
        if (viewMode === "map" && membersContainerRef.current) {
            membersContainerRef.current.scrollTop = 0
        }
    }, [viewMode])

    useEffect(() => {
        if (!effectiveRoomId) {
            return
        }

        const storageKey = `room-members-view-mode-${effectiveRoomId}`
        const storedViewMode = sessionStorage.getItem(storageKey)

        if (storedViewMode === "map" || storedViewMode === "list") {
            setViewMode(storedViewMode)
        }
    }, [effectiveRoomId])

    const onRoomMemberClick = (memberId: number) => {
        switch (role) {
            case RoleType.USER:
                if (userDataState.id === memberId) {
                    navigate("/user-profile")
                } else {
                    navigate(`/user/${memberId}`)
                }
                break
            case RoleType.ORGANIZATION:
                if (userDataState.id === memberId) {
                    navigate("/user-profile")
                } else {
                    navigate(`/user/${memberId}/${id}`)
                }
                break
            default:
                break
        }
    }

    const onRemoveIconClick = () => {
        setInputState("")
    }

    const onOrganizationRoomMemberClick = (memberId: number) => {
        navigate(`/organization-profile/${memberId}`)
    }

    const onRemoveRoomMemberModalOpen = (id: number, type: string) => {
        dispatch(setRemoveRoomMemberId({ id: id, type: type }))
        dispatch(setModalData({ open: true, removeRoomMemberModal: true }))
    }

    const onMemberOpenProfile = (member: { id: number; type: string }) => {
        let targetPath = ""

        if (member.type === RoleType.USER.toLowerCase()) {
            if (userDataState.id === member.id) {
                targetPath = "/user-profile"
            } else if (role === RoleType.ORGANIZATION) {
                targetPath = `/user/${member.id}/${id}`
            } else {
                targetPath = `/user/${member.id}`
            }
        } else {
            targetPath = `/organization-profile/${member.id}`
        }

        if (!targetPath || targetPath.includes("undefined")) {
            return
        }

        window.location.assign(targetPath)
    }

    return (
        <div className="room-members" style={{ overflow: 'hidden' }}>
            <div className="room-members__toolbar">
                <div className="room-members__toolbar-search">
                    <SearchInput
                        onRemoveIconClick={onRemoveIconClick}
                        searchvalue={inputState}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setInputState(event.target.value)}
                        isGlobalSearch={false}
                    />
                </div>
                <div className="room-members__view-toggle">
                    <button
                        className={`room-members__view-button ${viewMode === "list" ? "room-members__view-button--active" : ""}`}
                        onClick={() => onViewModeChange("list")}
                    >
                        Liste
                    </button>
                    <button
                        className={`room-members__view-button ${viewMode === "map" ? "room-members__view-button--active" : ""}`}
                        onClick={() => onViewModeChange("map")}
                    >
                        Karte
                    </button>
                </div>
            </div>
            <div className="room-members__counter">
                {roomDataState.total_members} {roomDataState.total_members && roomDataState.total_members > 1 ? translation.members : translation.member}
                {inputState !== "" && (
                    <> insgesamt ({Members.length} Treffer) </>
                )}</div>
            <div className="room-members__container" ref={membersContainerRef} style={{ overflow: viewMode === "map" ? "hidden" : "auto" }}>
                {isMemberInRoom && !canSeeMembers ? (
                    <div className="room-members__no-access"> {translation.onlyMembersCanSeeOtherMembers} </div>
                ) : (
                    viewMode === "map" ? (
                        <RoomMembersMap members={Members} onMemberClick={onMemberOpenProfile} />
                    ) : (
                        Members?.map((member, index) => {
                            return (
                                <div className="room-members__items" key={member.id + member.type} style={{ overflow: 'hidden' }}>
                                    <UserCard
                                        imageSrc={member.profile_photo?.url_path || (member.type === RoleType.USER.toLowerCase() ? profileDefault : organizationProfileDefault)}
                                        text={member.name}
                                        badge={member.room_role === 'member' ? 'Mitglied' : 'Moderator:in'}
                                        subtextStyle="tag-container"
                                        profileImageStyle="image image__profile image__profile--fit image__profile--size-small "
                                        userCardStyle={`${index === Members.length - 1 ? "room-members-item-no-border" : "room-members-item-border"}`}
                                        textStyle={"text"}
                                        textStyleMore25={"text"}
                                        middlePanelStyle={"middle-panel"}
                                        rightText={
                                            hasPrivelegedPermission ? (
                                                <div className="room-members__actions">
                                                    {['manager', 'member'].includes(member.room_role!) && (
                                                        <div className="room-members__icon" onClick={() => dispatch(setModalData({ open: true, roomMemberRoleModal: { status: true, member: member, memberType: member.type, roomId: Number(effectiveRoomId), role: member.room_role! } }))} >
                                                            <LockerIconSmall />
                                                        </div>
                                                    )}

                                                    <div className="room-members__icon" onClick={() => onRemoveRoomMemberModalOpen(member.id!, member.type)}>
                                                        <CloseIcon />
                                                    </div>
                                                </div>

                                            ) : null
                                        }
                                        leftPanelStyle={"left-panel"}
                                        rightPanelStyle={"right-panel"}
                                        onMiddleItemClick={() => member.type === RoleType.USER.toLowerCase() ? onRoomMemberClick(member.id) : onOrganizationRoomMemberClick(member.id)}
                                        onLeftItemClick={() => member.type === RoleType.USER.toLowerCase() ? onRoomMemberClick(member.id) : onOrganizationRoomMemberClick(member.id)}
                                    />
                                </div>
                            )
                        })
                    )
                )}
            </div>
        </div>
    )
}

export default RoomMembers